import Swal from "sweetalert2";
import { showAlert } from "helper";
import styled from 'styled-components';
import Panel from "@/shared/components/Panel";
import DeleteIcon from 'mdi-react/DeleteIcon';
import React, { useEffect, useState } from 'react';
import { paddingLeft, paddingRight } from '@/utils/directions';
import useTaskingStore from '../../../../../store-zustand/tasking';
import { colorAdditional, colorGreenHover } from '@/utils/palette';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { getAreaFromCartographicCoordinates } from "components/MapView/MapUtils";

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover svg {
    fill: ${colorGreenHover};
  }

  &:hover svg.orange-hover {
    fill: #e9c886;
  }

  &:hover svg.red-hover {
    fill: #e98686;
  }
`;

const CaptureRequestsList = () => {
    const { getTasks, deleteTask } = useTaskingStore();
    const [captureRequestsList, setCaptureRequestsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: true,
        withSearchEngine: true,
        manualPageSize: [10, 20, 30, 40],
        placeholder: 'Search...',
    };

    const handleDelete = (request_id) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the capture request (id: ${request_id})?`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                deleteTask(request_id).then((response) => {
                    showAlert('success', response);
                    GetCaptureRequestsData();
                }).catch((error) => {
                    showAlert('error', `Error in deleting the capture requets. ${error}`);
                })
            }
        });
    }

    const calculateAreaForCaptureRequest = (request) => {
        const wkt_coordinates = request.fields.Geometry.replace(/^SRID=4326;POLYGON Z\s*\(\(/, '').replace(/\)\)$/, '');
        const coordinates = wkt_coordinates.split(',').map(pair => {
            const [longitude, latitude] = pair.trim().split(' ').map(Number);
            return [longitude, latitude];
        });
        
        let area = '';
        if (coordinates.length >= 3) {
            if (coordinates[0][0] !== coordinates[coordinates.length-1][0] || coordinates[0][1] !== coordinates[coordinates.length-1][1]) {
                coordinates.push([coordinates[0][0], coordinates[0][1]]);
            }

            area = getAreaFromCartographicCoordinates(coordinates);
            if (area > 100000) {
                area = `${(area/1e6).toFixed(2)} km²`;
            } else if (area > 1) {
                area = `${area.toFixed(2)} m²`;
            } else {
                area = `${area} m²`;
            }
        }
        return area;
    }

    const GetCaptureRequestsData = () => {
        getTasks().then((requests) => {
            if (requests === 'Error') {
                showAlert('error', 'Error in getting capture requests.');
                return;
            }
            const columns = [
                {
                    accessor: 'id',
                    Header: 'ID',
                    disableGlobalFilter: true,
                    disableSortBy: false,
                },
                {
                    accessor: 'user',
                    Header: 'User',
                },
                {
                    accessor: 'date',
                    Header: 'Date',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'type',
                    Header: 'Type',
                },
                {
                    accessor: 'resolution',
                    Header: 'Resolution',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'bundle',
                    Header: 'Bundle',
                },
                {
                    accessor: 'update',
                    Header: 'Update',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'road_network',
                    Header: 'Road Network',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'scan_space',
                    Header: 'Scan Space',
                    disableGlobalFilter: true,
                },
                {
                    accessor: 'area',
                    Header: 'ROI Area',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'price',
                    Header: 'Estimated Price',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            
            requests.forEach(request => {
                const area = calculateAreaForCaptureRequest(request);
                data.push({
                    id: request.pk,
                    user: request.fields.User,
                    date: new Date(`${request.fields.TaskDate}`).toLocaleString(),
                    type: request.fields.Type,
                    resolution: request.fields.Resolution,
                    bundle: request.fields.Bundle,
                    update: request.fields.Update,
                    road_network: request.fields.Road_Network,
                    scan_space: request.fields.Scan_Space,
                    area: area,
                    price: 'CAD $' + 0,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(request.pk)}><DeleteIcon className="red-hover"/></CartTableButton>,
                });
            });

            const captureRequestsListTableData = { tableHeaderData: columns, tableRowsData: data };
            setCaptureRequestsList(captureRequestsListTableData);
        }).catch((error) => {
            showAlert('error', `Error in getting capture requests. ${error}`)
        })
    };

    useEffect(() => {
        GetCaptureRequestsData();
    }, []);

    return (
        <Panel lg={12} md={12} title='Capture Requests List' refreshRequest={() => GetCaptureRequestsData()}>
            <ReactTableBase
                columns={captureRequestsList.tableHeaderData}
                data={captureRequestsList.tableRowsData}
                tableConfig={tableConfig}
            />
        </Panel>
    );
};

export default CaptureRequestsList;